import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faSave,
  faSpinner,
  faTimes,
  faPlus,
  faCircleCheck,
  faCircleXmark,
  faFileText
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import ErrorModal from '../../components/ErrorModal';
import { useAuth } from '../../contexts/AuthContext';

const SurveyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isChargeEtude = user && (user.role === 'charge_etude' || user.role === 'charge_etude_superieur' || user.role === 'resp_controle' || user.role === 'chef_terrain_superieur');
  const isChefTerrain = user && user.role === 'chef_terrain';
  // supervisor_cati: read-only, no Modifier button
  const readOnly = user?.role === 'supervisor_cati';

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const wavesEditorRef = useRef(null);

  // Edit form data
  const [formData, setFormData] = useState({});

  // Client states
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatingClient, setCreatingClient] = useState(false);

  useEffect(() => {
    fetchSurvey();
    fetchClients();
  }, [id]);

  /**
   * `silent` : rechargement après un enregistrement réussi. Pas de voile de chargement (il
   * couvrirait toute la page) et pas d'écran d'erreur — la sauvegarde, elle, a abouti : en cas
   * d'échec du rechargement on garde l'objet précédent plutôt que d'alarmer à tort.
   */
  const fetchSurvey = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(`/cati/surveys/${id}`);
      if (response.data.success) {
        setSurvey(response.data.data);
      } else if (!silent) {
        setError('Enquête non trouvée');
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
      if (!silent) setError('Erreur lors du chargement de l\'enquête');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/cati/clients');
      if (response.data.success) {
        setClients(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const enterEditMode = () => {
    setFormData({
      title: survey.title || '',
      poste_qcode: survey.poste_qcode || '',
      price_per_complete: survey.price_per_complete || '',
      price_per_day: survey.price_per_day || '',
      target_days: survey.target_days || '',
      target_interviewers: survey.target_interviewers || '',
      is_active: survey.is_active,
      client_id: survey.client_id || null,
      general_target: survey.general_target || '',
      date_debut: survey.date_debut ? survey.date_debut.split('T')[0] : '',
      date_fin: survey.date_fin ? survey.date_fin.split('T')[0] : '',
      control_percentage: survey.control_percentage !== null && survey.control_percentage !== undefined ? survey.control_percentage : '',
      marge_imprevus: survey.marge_imprevus !== null && survey.marge_imprevus !== undefined ? survey.marge_imprevus : '',
      objectif_quotidien: survey.objectif_quotidien !== null && survey.objectif_quotidien !== undefined ? survey.objectif_quotidien : '',
      consignes: survey.consignes || '',
      is_tracker: !!survey.is_tracker,
      waves: (survey.waves || []).map(w => ({
        id: w.id,
        label: w.label || '',
        date_debut: w.date_debut ? String(w.date_debut).split('T')[0] : '',
        date_fin: w.date_fin ? String(w.date_fin).split('T')[0] : '',
        target: w.target != null ? w.target : '',
        marge_imprevus: w.marge_imprevus != null ? w.marge_imprevus : ''
      }))
    });
    setSelectedClient(survey.client || null);
    setClientSearch(survey.client?.name || '');
    setErrors({});
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setErrors({});
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Tracker (vagues) — F2F surveys only. In edit mode use the form flag,
  // otherwise reflect the saved survey.
  // Trackers apply to CATI and F2F alike (see CreateSurvey).
  const isTracker = isEditing ? formData.is_tracker : survey?.is_tracker;
  const wavesTargetSum = (formData.waves || []).reduce((sum, w) => sum + (parseInt(w.target) || 0), 0);
  const wavesMargeSum = (formData.waves || []).reduce((sum, w) => sum + (parseFloat(w.marge_imprevus) || 0), 0);

  const handleToggleTracker = (checked) => {
    // Seed Vague 1 from the existing quota that will be adopted (so conversion
    // is non-destructive), falling back to the survey's own dates / objective.
    const adopt = survey?.adoptable_quota;
    setFormData(prev => ({
      ...prev,
      is_tracker: checked,
      waves: checked && (!prev.waves || prev.waves.length === 0)
        ? [{
            label: '',
            date_debut: (adopt?.fieldwork_start_date) || prev.date_debut || '',
            date_fin: (adopt?.fieldwork_end_date) || prev.date_fin || '',
            target: adopt?.overall_target != null ? String(adopt.overall_target) : (prev.general_target || ''),
            marge_imprevus: prev.marge_imprevus != null ? String(prev.marge_imprevus) : ''
          }]
        : (prev.waves || [])
    }));
  };

  const addWave = () => {
    setFormData(prev => ({
      ...prev,
      waves: [...(prev.waves || []), { label: '', date_debut: '', date_fin: '', target: '' }]
    }));
  };

  const removeWave = (idx) => {
    setFormData(prev => ({
      ...prev,
      waves: (prev.waves || []).filter((_, i) => i !== idx)
    }));
  };

  const updateWave = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      waves: (prev.waves || []).map((w, i) => (i === idx ? { ...w, [field]: value } : w))
    }));
    if (errors.waves) {
      setErrors(prev => ({ ...prev, waves: '' }));
    }
  };

  // Client handlers
  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, client_id: client.id }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, client_id: null }));
    setClientSearch('');
  };

  const handleCreateClient = async (name) => {
    try {
      setCreatingClient(true);
      const response = await api.post('/cati/clients', { name });
      if (response.data.success) {
        const newClient = response.data.data;
        setClients(prev => [...prev, newClient].sort((a, b) => a.name.localeCompare(b.name)));
        handleClientSelect(newClient);
      }
    } catch (error) {
      if (error.response?.data?.errors?.name) {
        setErrors(prev => ({ ...prev, client_id: error.response.data.errors.name[0] }));
      }
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    const isPerDay = survey?.payment_type === 'per_day';
    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }
    if (isPerDay) {
      if (!formData.price_per_day || parseFloat(formData.price_per_day) <= 0) {
        newErrors.price_per_day = 'Le prix par jour doit être supérieur à 0';
      }
      if (!formData.target_days || parseInt(formData.target_days) <= 0) {
        newErrors.target_days = 'Le nombre de jours est requis';
      }
      if (!formData.target_interviewers || parseInt(formData.target_interviewers) <= 0) {
        newErrors.target_interviewers = 'Le nombre d\'enquêteurs est requis';
      }
    } else if (!formData.price_per_complete || parseFloat(formData.price_per_complete) <= 0) {
      newErrors.price_per_complete = 'Le prix doit être supérieur à 0';
    }
    if (isTracker) {
      const waves = formData.waves || [];
      if (waves.length < 1) {
        newErrors.waves = 'Ajoutez au moins une vague';
      } else {
        const sorted = waves
          .filter(w => w.date_debut)
          .slice()
          .sort((a, b) => a.date_debut.localeCompare(b.date_debut));
        for (const w of waves) {
          if (!w.date_debut || !w.date_fin) {
            newErrors.waves = 'Chaque vague doit avoir une date de début et une date de fin';
            break;
          }
          if (w.date_fin < w.date_debut) {
            newErrors.waves = 'La date de fin d\'une vague doit être après sa date de début';
            break;
          }
          if (!w.target || parseInt(w.target) <= 0) {
            newErrors.waves = 'Chaque vague doit avoir un objectif (complétions) supérieur à 0';
            break;
          }
        }
        if (!newErrors.waves) {
          for (let i = 1; i < sorted.length; i++) {
            if (sorted[i].date_debut <= sorted[i - 1].date_fin) {
              newErrors.waves = 'Les fenêtres des vagues ne doivent pas se chevaucher';
              break;
            }
          }
        }
      }
    } else if (formData.date_debut && formData.date_fin && formData.date_fin < formData.date_debut) {
      newErrors.date_fin = 'La date de fin doit être après la date de début';
    }
    if (survey?.poste_qcode === 'ref' && formData.objectif_quotidien !== '' && formData.objectif_quotidien !== null) {
      const oq = parseInt(formData.objectif_quotidien);
      if (isNaN(oq) || oq < 1) {
        newErrors.objectif_quotidien = "L'objectif quotidien doit être supérieur à 0";
      }
    }
    if (formData.control_percentage !== '' && formData.control_percentage !== null) {
      const cp = parseFloat(formData.control_percentage);
      if (isNaN(cp) || cp < 0 || cp > 100) {
        newErrors.control_percentage = 'Le pourcentage doit être entre 0 et 100';
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Bring the vagues error into view (it can be below the fold).
      if (newErrors.waves && wavesEditorRef.current) {
        setTimeout(() => {
          wavesEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      return;
    }

    setSaving(true);
    try {
      const isPerDay = survey?.payment_type === 'per_day';
      const submitData = {
        title: formData.title,
        is_active: formData.is_active,
        client_id: formData.client_id || null,
        is_tracker: isTracker,
        date_debut: isTracker ? null : (formData.date_debut || null),
        date_fin: isTracker ? null : (formData.date_fin || null),
        control_percentage: formData.control_percentage !== '' ? parseFloat(formData.control_percentage) : null,
        marge_imprevus: formData.marge_imprevus !== '' && formData.marge_imprevus !== null ? parseFloat(formData.marge_imprevus) : null,
      };
      // Objectif quotidien + consignes are F2F-only; a CATI survey never sends them.
      if (survey?.poste_qcode === 'ref') {
        submitData.objectif_quotidien = formData.objectif_quotidien !== '' && formData.objectif_quotidien !== null
          ? parseInt(formData.objectif_quotidien)
          : null;
        submitData.consignes = formData.consignes && formData.consignes.trim() !== ''
          ? formData.consignes.trim()
          : null;
      }
      submitData.general_target = isTracker ? null : (formData.general_target ? parseInt(formData.general_target) : null);
      if (isTracker) {
        submitData.waves = (formData.waves || []).map(w => ({
          id: w.id,
          label: w.label || null,
          date_debut: w.date_debut,
          date_fin: w.date_fin,
          target: parseInt(w.target),
          marge_imprevus: w.marge_imprevus !== '' && w.marge_imprevus != null ? parseFloat(w.marge_imprevus) : 0
        }));
      }
      if (isPerDay) {
        submitData.price_per_day = parseFloat(formData.price_per_day);
        submitData.target_days = parseInt(formData.target_days);
        submitData.target_interviewers = parseInt(formData.target_interviewers);
      } else {
        submitData.price_per_complete = parseFloat(formData.price_per_complete);
      }

      const response = await api.put(`/cati/surveys/${id}`, submitData);
      if (response.data.success) {
        // La réponse de PUT ne charge que 'client' : s'en servir ferait disparaître les vagues
        // (et tout ce que le GET enrichit) jusqu'au prochain rafraîchissement. On recharge donc
        // le modèle complet, qui est la seule source de vérité pour l'affichage.
        await fetchSurvey({ silent: true });
        setIsEditing(false);
        setSuccessMessage('Enquête modifiée avec succès');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error updating survey:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        if (error.response.data.errors.waves) {
          // The save was blocked → nothing persisted. Restore any vague that was
          // removed from the form (e.g. it still has a quota/frais) so the UI
          // matches the saved state and the user isn't confused.
          setFormData(prev => {
            const presentIds = new Set((prev.waves || []).map(w => w.id).filter(Boolean));
            const restored = (survey.waves || [])
              .filter(w => !presentIds.has(w.id))
              .map(w => ({
                id: w.id,
                label: w.label || '',
                date_debut: w.date_debut ? String(w.date_debut).split('T')[0] : '',
                date_fin: w.date_fin ? String(w.date_fin).split('T')[0] : '',
                target: w.target != null ? w.target : '',
                marge_imprevus: w.marge_imprevus != null ? w.marge_imprevus : '',
              }));
            if (restored.length === 0) return prev;
            const merged = [...(prev.waves || []), ...restored]
              .sort((a, b) => (a.date_debut || '').localeCompare(b.date_debut || ''));
            return { ...prev, waves: merged };
          });
          if (wavesEditorRef.current) {
            setTimeout(() => {
              wavesEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
          }
        }
      } else if (error.response?.data?.message) {
        // Show blocking errors (e.g. can't disable tracker while vague quotas
        // exist) as a modal so it can't be missed.
        setErrorModal({ show: true, message: error.response.data.message });
        // If turning tracker OFF was blocked, the save didn't persist — restore
        // the form to the saved state (tracker back on with its vagues) so it
        // isn't left showing tracker disabled.
        if (survey?.is_tracker && !formData.is_tracker) {
          setFormData(prev => ({
            ...prev,
            is_tracker: true,
            waves: (survey.waves || []).map(w => ({
              id: w.id,
              label: w.label || '',
              date_debut: w.date_debut ? String(w.date_debut).split('T')[0] : '',
              date_fin: w.date_fin ? String(w.date_fin).split('T')[0] : '',
              target: w.target != null ? w.target : '',
              marge_imprevus: w.marge_imprevus != null ? w.marge_imprevus : '',
            })),
          }));
        }
      } else {
        setErrors({ general: 'Erreur lors de la modification de l\'enquête' });
      }
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price) + ' MAD';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10 py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'enquête...</p>
        </div>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10 py-20">
          <p className="text-red-600 text-lg">{error || 'Enquête non trouvée'}</p>
          <button
            onClick={() => navigate('/cati/surveys')}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retour aux enquêtes
          </button>
        </div>
      </div>
    );
  }

  // Objectif Quotidien (F2F uniquement) : rendu juste après l'Objectif Général, qui vit dans
  // deux endroits distincts selon que l'enquête est tracker ou non. Défini ici une seule fois
  // pour éviter d'en dupliquer le balisage dans les deux branches.
  const objectifQuotidienField = survey.poste_qcode !== 'ref' ? null : (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-1">Objectif Quotidien</label>
      {isEditing ? (
        <>
          <input
            type="number"
            name="objectif_quotidien"
            value={formData.objectif_quotidien}
            onChange={handleInputChange}
            min="1"
            placeholder="Ex: 8"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
              errors.objectif_quotidien ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.objectif_quotidien && (
            <p className="mt-1 text-sm text-red-600">{errors.objectif_quotidien}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">Complétions attendues par enquêteur et par jour</p>
        </>
      ) : (
        <p className="text-gray-900 font-medium">
          {survey.objectif_quotidien ? `${survey.objectif_quotidien} complétions / jour` : '-'}
        </p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden py-4 sm:py-8">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`text-xl sm:text-2xl font-bold text-gray-900 border-b-2 focus:outline-none focus:border-red-500 bg-transparent w-full ${
                        errors.title ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{survey.title}</h1>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      survey.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <FontAwesomeIcon
                        icon={survey.is_active ? faCircleCheck : faCircleXmark}
                        className="mr-1"
                      />
                      {survey.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-500">
                      ID LimeSurvey: {survey.limesurvey_id}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {!readOnly && (
                    isEditing ? (
                      <div className="flex gap-2">
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center text-sm"
                        >
                          {saving ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Enregistrement...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSave} className="mr-2" />
                              Enregistrer
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={enterEditMode}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center text-sm"
                      >
                        <FontAwesomeIcon icon={faPen} className="mr-2" />
                        Modifier
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
            {errors.title && (
              <p className="mt-2 text-sm text-red-600">{errors.title}</p>
            )}
            {errors.general && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}
          </div>
        </div>

        {/* Information Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 sm:p-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4 sm:mb-6">Informations Générales</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* LimeSurvey ID - always read only */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">ID LimeSurvey</label>
              <p className="text-gray-900 font-medium">{survey.limesurvey_id}</p>
            </div>

            {/* Nature de l'enquête */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Nature</label>
              {isEditing ? (
                // Type is locked after creation — display the survey's actual type as a badge
                survey.poste_qcode === 'ref' ? (
                  <div className="px-3 py-2 border-2 border-blue-500 bg-blue-50 text-blue-700 rounded-lg text-center font-medium text-sm">
                    Face à Face
                  </div>
                ) : (
                  <div className="px-3 py-2 border-2 border-red-500 bg-red-50 text-red-700 rounded-lg text-center font-medium text-sm">
                    CATI
                  </div>
                )
              ) : (
                <p className="text-gray-900 font-medium">
                  {survey.poste_qcode === 'ref' ? 'Face à Face' : survey.poste_qcode === 'Poste' ? 'CATI' : (survey.poste_qcode || '-')}
                </p>
              )}
            </div>

            {/* Type de Paiement (read-only — locked after creation) */}
            {survey.poste_qcode !== 'ref' && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Type de Paiement</label>
                <p className="text-gray-900 font-medium">
                  {survey.payment_type === 'per_day' ? 'À la journée' : 'Au questionnaire'}
                </p>
              </div>
            )}

            {/* Price (per_complete) */}
            {survey.payment_type !== 'per_day' && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Prix par Complétion</label>
                {isEditing ? (
                  <>
                    <input
                      type="number"
                      name="price_per_complete"
                      value={formData.price_per_complete}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0.01"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.price_per_complete ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.price_per_complete && (
                      <p className="mt-1 text-sm text-red-600">{errors.price_per_complete}</p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-900 font-medium">{formatPrice(survey.price_per_complete)}</p>
                )}
              </div>
            )}

            {/* Per-day fields */}
            {survey.payment_type === 'per_day' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Prix par Jour</label>
                  {isEditing ? (
                    <>
                      <input
                        type="number"
                        name="price_per_day"
                        value={formData.price_per_day}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0.01"
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                          errors.price_per_day ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.price_per_day && (
                        <p className="mt-1 text-sm text-red-600">{errors.price_per_day}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-900 font-medium">{formatPrice(survey.price_per_day)}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Nombre de Jours</label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="target_days"
                      value={formData.target_days}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.target_days ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{survey.target_days || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Nombre d'Enquêteurs</label>
                  {isEditing ? (
                    <input
                      type="number"
                      name="target_interviewers"
                      value={formData.target_interviewers}
                      onChange={handleInputChange}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.target_interviewers ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{survey.target_interviewers || '-'}</p>
                  )}
                </div>
              </>
            )}

            {/* Frais de Déplacement - only for F2F, read-only (auto-updated on validation) */}
            {survey.poste_qcode === 'ref' && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Frais de Déplacement</label>
                <p className="text-gray-900 font-medium">{formatPrice(survey.frais_deplacement)}</p>
                <p className="mt-1 text-xs text-gray-400">Mis à jour automatiquement lors de la validation des frais de déplacement</p>
              </div>
            )}

            {/* Marge Imprévus - F2F only. Tracker sets it per vague, so hide the
                survey-level input while editing a tracker survey (view still
                shows the rolled-up total). */}
            {survey.poste_qcode === 'ref' && !(isEditing && isTracker) && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Marge Imprévus</label>
                {isEditing ? (
                  <>
                    <input
                      type="number"
                      name="marge_imprevus"
                      value={formData.marge_imprevus}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="Ex: 5000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <p className="mt-1 text-xs text-gray-400">Budget pour les dépassements de circuits + bonus</p>
                  </>
                ) : (
                  <p className="text-gray-900 font-medium">{formatPrice(survey.marge_imprevus)}</p>
                )}
              </div>
            )}

            {/* Tracker (vagues) switch — edit mode, CATI and F2F alike */}
            {isEditing && (
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between p-3 border-2 border-blue-100 rounded-lg bg-blue-50/30">
                  <div className="pr-4">
                    <span className="block text-sm font-bold text-gray-700">Enquête tracker (vagues)</span>
                    <span className="block text-xs text-gray-500">
                      Plusieurs vagues successives ; l'objectif général est calculé automatiquement.
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!formData.is_tracker}
                      onChange={(e) => handleToggleTracker(e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>
              </div>
            )}

            {(isEditing && isTracker) ? (
              /* Vagues editor */
              <div ref={wavesEditorRef} className="sm:col-span-2 space-y-3 p-3 border-2 border-blue-100 rounded-lg bg-blue-50/20">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">Vagues *</span>
                  <button
                    type="button"
                    onClick={addWave}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Ajouter une vague
                  </button>
                </div>

                {survey?.adoptable_quota && (
                  <div className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    Le quota existant (objectif {Number(survey.adoptable_quota.overall_target || 0).toLocaleString('fr-FR')}) deviendra la <strong>Vague 1</strong>.
                    Gardez cet objectif pour l'ajouter tel quel, ou réduisez-le pour le répartir entre les vagues.
                  </div>
                )}

                {errors.waves && (
                  <p className="text-sm text-red-600">{errors.waves}</p>
                )}

                {(formData.waves || []).map((w, idx) => (
                  <div key={idx} className="p-3 bg-white border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">{w.label || `Vague ${idx + 1}`}</span>
                      {(formData.waves || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeWave(idx)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer la vague"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${survey.poste_qcode === 'ref' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date de début</label>
                        <input
                          type="date"
                          value={w.date_debut}
                          onChange={(e) => updateWave(idx, 'date_debut', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date de fin</label>
                        <input
                          type="date"
                          value={w.date_fin}
                          onChange={(e) => updateWave(idx, 'date_fin', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Objectif (complétions)</label>
                        <input
                          type="number"
                          min="1"
                          value={w.target}
                          onChange={(e) => updateWave(idx, 'target', e.target.value)}
                          placeholder="Ex: 250"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {/* Marge imprévus — F2F only (travel-cost concept). */}
                      {survey.poste_qcode === 'ref' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marge imprévus (MAD)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={w.marge_imprevus}
                          onChange={(e) => updateWave(idx, 'marge_imprevus', e.target.value)}
                          placeholder="Ex: 5000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between px-4 py-3 bg-white border border-blue-200 rounded-lg">
                  <span className="text-sm font-bold text-gray-700">Objectif Général (complétions)</span>
                  <span className="text-lg font-bold text-blue-700">{wavesTargetSum.toLocaleString('fr-FR')}</span>
                </div>
                <p className="text-xs text-gray-500">Calculé automatiquement à partir des objectifs des vagues.</p>

                {objectifQuotidienField}

                {survey.poste_qcode === 'ref' && (
                  <>
                <div className="flex items-center justify-between px-4 py-3 bg-white border border-blue-200 rounded-lg">
                  <span className="text-sm font-bold text-gray-700">Marge Imprévus (total)</span>
                  <span className="text-lg font-bold text-blue-700">{wavesMargeSum.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD</span>
                </div>
                <p className="text-xs text-gray-500">Somme des marges imprévus des vagues.</p>
                  </>
                )}
              </div>
            ) : (
              <>
                {/* General Target */}
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Objectif Général</label>
                    {isEditing ? (
                      <input
                        type="number"
                        name="general_target"
                        value={formData.general_target}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="Ex: 500"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    ) : (
                      <p className="text-gray-900 font-medium">
                        {survey.general_target ? `${survey.general_target.toLocaleString('fr-FR')} complétions` : '-'}
                      </p>
                    )}
                </div>

                {objectifQuotidienField}

                {/* Date Debut */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Date de Début</label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="date_debut"
                      value={formData.date_debut}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  ) : (
                    <p className="text-gray-900 font-medium">{formatDate(survey.date_debut)}</p>
                  )}
                </div>

                {/* Date Fin */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Date de Fin</label>
                  {isEditing ? (
                    <>
                      <input
                        type="date"
                        name="date_fin"
                        value={formData.date_fin}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                          errors.date_fin ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.date_fin && (
                        <p className="mt-1 text-sm text-red-600">{errors.date_fin}</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-900 font-medium">{formatDate(survey.date_fin)}</p>
                  )}
                </div>
              </>
            )}

            {/* Vagues breakdown (read-only view) */}
            {!isEditing && survey.is_tracker && (survey.waves || []).length > 0 && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-2">Vagues</label>
                <div className="space-y-2">
                  {survey.waves.map((w, idx) => {
                    // Realised completions for the vague's own date window, from the API.
                    const target = Number(w.target || 0);
                    const done = Number(w.completions || 0);
                    const pct = target > 0 ? Math.round((done / target) * 100) : 0;
                    const barColor = pct >= 100 ? 'bg-green-600' : pct >= 50 ? 'bg-blue-600' : 'bg-amber-500';
                    return (
                      <div key={w.id || idx} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-700">{w.label || `Vague ${idx + 1}`}</span>
                          <span className="text-sm text-gray-600">{formatDate(w.date_debut)} → {formatDate(w.date_fin)}</span>
                          <span className="text-sm font-medium text-blue-700">
                            {done.toLocaleString('fr-FR')} / {target.toLocaleString('fr-FR')} complétions
                            {target > 0 && <span className="text-gray-500 font-normal"> ({pct}%)</span>}
                            {w.completions_frozen && <span className="text-gray-500 font-normal"> (quota terminé)</span>}
                          </span>
                          {survey.poste_qcode === 'ref' && (
                            <span className="text-sm text-gray-500">Marge: {formatPrice(w.marge_imprevus)}</span>
                          )}
                        </div>
                        {target > 0 && (
                          <div className="mt-2 w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-1.5 ${barColor} rounded-full`} style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Consignes — F2F only */}
            {survey.poste_qcode === 'ref' && (
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-1">Consignes</label>
                {isEditing ? (
                  <>
                    <textarea
                      name="consignes"
                      value={formData.consignes}
                      onChange={handleInputChange}
                      rows={6}
                      placeholder="Consignes de terrain pour cette enquête..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <p className="mt-1 text-xs text-gray-400">Instructions destinées aux équipes terrain</p>
                  </>
                ) : (
                  survey.consignes ? (
                    <p className="text-gray-900 whitespace-pre-wrap break-words">{survey.consignes}</p>
                  ) : (
                    <p className="text-gray-900 font-medium">-</p>
                  )
                )}
              </div>
            )}

            {/* Client */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-500 mb-1">Client</label>
              {isEditing ? (
                <div className="relative">
                  {selectedClient ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 px-3 py-2 border border-green-300 bg-green-50 rounded-lg text-green-800 font-medium">
                        {selectedClient.name}
                      </div>
                      <button
                        type="button"
                        onClick={handleClearClient}
                        className="px-3 py-2 text-gray-500 hover:text-red-600 transition-colors"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                        placeholder="Rechercher ou créer un client..."
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                          errors.client_id ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {showClientDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {filteredClients.length > 0 ? (
                            filteredClients.map(client => (
                              <div
                                key={client.id}
                                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  handleClientSelect(client);
                                }}
                              >
                                <div className="text-sm font-medium text-gray-900">{client.name}</div>
                              </div>
                            ))
                          ) : !clientSearch.trim() ? (
                            <div className="px-4 py-3 text-gray-500 text-center text-sm">
                              Tapez pour rechercher...
                            </div>
                          ) : null}

                          {clientSearch.trim() && !clients.find(c => c.name.toLowerCase() === clientSearch.trim().toLowerCase()) && (
                            <div
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-t border-gray-200 text-blue-600"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleCreateClient(clientSearch.trim());
                              }}
                            >
                              {creatingClient ? (
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                                  Création...
                                </span>
                              ) : (
                                <span className="flex items-center">
                                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                  Créer le client "{clientSearch.trim()}"
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {errors.client_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-900 font-medium">{survey.client?.name || '-'}</p>
              )}
            </div>

            {/* Pourcentage de Contrôle */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Pourcentage de Contrôle (%)</label>
              {isEditing ? (
                <>
                  <input
                    type="number"
                    name="control_percentage"
                    value={formData.control_percentage}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="Ex: 15"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  {errors.control_percentage && (
                    <p className="mt-1 text-sm text-red-600">{errors.control_percentage}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-900 font-medium">
                  {survey.control_percentage != null ? `${survey.control_percentage}%` : '-'}
                </p>
              )}
            </div>

            {/* Status */}
            {isEditing && (
              <div className="sm:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Enquête active</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />

      {/* Error Modal (e.g. blocked tracker change) */}
      <ErrorModal
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, message: '' })}
        title="Modification impossible"
        message={errorModal.message}
      />
    </div>
  );
};

export default SurveyDetail;
